import AuthPageFrame from '../AuthPageFrame';
import { Button } from '@/components/ui/button';
import { captureUtmParams, clearStoredUtmParams, getStoredUtmParams } from '../../model/utm';
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Eye, EyeOff } from 'lucide-react';
import { getErrorMessage } from '@/shared/lib/getErrorMessage';
import { getNewPasswordError, usePasswordPolicy } from '../../model/passwordPolicy';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PhoneInput } from '@/components/ui/phone-input';
import { reachGoal } from '@/shared/lib/yandexMetrika';
import { toast } from '@/components/ui/sonner';
import { useAuthStore } from '../../model/authStore';
import { useMutation } from '@tanstack/react-query';

const EMAIL_ALREADY_REGISTERED_ERROR = 'Пользователь с такой почтой уже зарегистрирован';
const PHONE_ALREADY_REGISTERED_ERROR = 'Пользователь с таким телефоном уже зарегистрирован.';
const PHONE_INVALID_FORMAT_ERROR = 'Введите корректный номер телефона';
const advertisingConsentUrl = 'https://white-line.pro/soglasie-reklama/';
const personalDataConsentUrl = 'https://white-line.pro/soglasie-na-obrabotku-personalnyh-dannyh/';
const personalDataPolicyUrl = 'https://white-line.pro/privacy-policy/';
const userAgreementUrl = 'https://white-line.pro/polzovatelskoe-soglashenie/';

const getPhoneError = (phone: string): string => {
  const isChina = phone.startsWith('+86');
  const dialCodeLength = isChina ? 2 : 1;
  const minPhoneLength = isChina ? 13 : 11;
  const phoneDigits = phone.replace(/\D/g, '');

  if (phoneDigits.length <= dialCodeLength) {
    return 'Введите номер телефона';
  }

  if (phoneDigits.length < minPhoneLength) {
    return 'Введите номер телефона полностью';
  }

  return '';
};

const getRegistrationConflictField = (error: unknown): 'email' | 'phone' | null => {
  if (!error || typeof error !== 'object' || !('response' in error)) return null;

  const response = (error as { response?: { data?: unknown; status?: number } }).response;
  if (response?.status !== 409) return null;

  const data = response.data;
  if (data && typeof data === 'object') {
    const message = (data as { error?: string; message?: string }).error
      || (data as { error?: string; message?: string }).message
      || '';
    const lower = message.toLowerCase();

    if (lower.includes('телефон') || lower.includes('phone')) {
      return 'phone';
    }

    if (lower.includes('почт') || lower.includes('email')) {
      return 'email';
    }
  }

  return 'email';
};

const getIsPhoneFormatError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object' || !('response' in error)) return false;

  const response = (error as { response?: { data?: unknown; status?: number } }).response;
  if (response?.status !== 400) return false;

  const data = response.data;
  if (data && typeof data === 'object') {
    const message = (data as { error?: string; message?: string }).error
      || (data as { error?: string; message?: string }).message
      || '';
    const lower = message.toLowerCase();

    return lower.includes('телефон') || lower.includes('phone');
  }

  return false;
};

const RegisterForm = () => {
  const [email, setEmail] = useState('');
  const [emailServerError, setEmailServerError] = useState('');
  const [isMarketingAccepted, setIsMarketingAccepted] = useState(false);
  const [isPdAccepted, setIsPdAccepted] = useState(false);
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneServerError, setPhoneServerError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [touched, setTouched] = useState({
    email: false,
    password: false,
    phone: false
  });

  const navigate = useNavigate();
  const location = useLocation();
  const register = useAuthStore((s) => s.register);
  const { data: dataGetPasswordPolicy, isError: isErrorGetPasswordPolicy, refetch: refetchGetPasswordPolicy } = usePasswordPolicy();

  const hasTrackedGoalRef = useRef(false);
  const emailError =
    !email.trim()
      ? 'Введите email'
      : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
        ? ''
        : 'Введите корректный email';
  const emailVisibleError = emailServerError || emailError;
  const passwordError = !password ? 'Введите пароль' : getNewPasswordError(password, dataGetPasswordPolicy);
  const pdConsentError = !isPdAccepted ? 'Подтвердите согласие на обработку персональных данных' : '';
  const phoneError = getPhoneError(phone);
  const phoneVisibleError = phoneServerError || phoneError;
  const canSubmit =
    !emailError
    && !passwordError
    && !phoneError
    && isPdAccepted;

  const {
    isPending: isPendingRegister,
    mutateAsync: mutateAsyncRegister
  } = useMutation({
    mutationFn: register,
    mutationKey: ['register', location.pathname],
    onError: (error) => {
      const conflictField = getRegistrationConflictField(error);
      const isPhoneFormat = getIsPhoneFormatError(error);

      if (conflictField === 'phone') {
        const message = getErrorMessage(error, PHONE_ALREADY_REGISTERED_ERROR);
        setPhoneServerError(message || PHONE_ALREADY_REGISTERED_ERROR);
        toast.error('Ошибка', {
          description: message || PHONE_ALREADY_REGISTERED_ERROR
        });
        return;
      }

      if (conflictField === 'email') {
        const message = getErrorMessage(error, EMAIL_ALREADY_REGISTERED_ERROR);
        setEmailServerError(message || EMAIL_ALREADY_REGISTERED_ERROR);
        toast.error('Ошибка', {
          description: message || EMAIL_ALREADY_REGISTERED_ERROR
        });
        return;
      }

      if (isPhoneFormat) {
        const message = getErrorMessage(error, PHONE_INVALID_FORMAT_ERROR);
        setPhoneServerError(message || PHONE_INVALID_FORMAT_ERROR);
        toast.error('Ошибка', {
          description: message || PHONE_INVALID_FORMAT_ERROR
        });
        return;
      }

      const errorMessage = getErrorMessage(error, 'Ошибка регистрации');
      toast.error('Ошибка', {
        description: errorMessage
      });
    },
    onSuccess: () => {
      if (!hasTrackedGoalRef.current) {
        hasTrackedGoalRef.current = true;
        reachGoal('registratsiya_forma');
      }
      clearStoredUtmParams();
      toast.success('Аккаунт создан', {
        description: 'Мы отправили вам письмо. Завершите регистрацию.',
        duration: 10_000
      });
      navigate('/', { replace: true });
    }
  });

  useEffect(() => {
    captureUtmParams(location.search);
  }, [location.search]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (!canSubmit || isPendingRegister) return;

    const utmParams = getStoredUtmParams();

    await mutateAsyncRegister({
      email: email.trim(),
      password,
      phone,
      ...utmParams
    }).catch(() => undefined);
  };

  const onChangeEmail = (event: ChangeEvent<HTMLInputElement>): void => {
    setEmail(event.target.value);
    setEmailServerError('');
  };

  const onChangePhone = (newPhone: string): void => {
    setPhone(newPhone);
    setPhoneServerError('');
  };

  return (
    <AuthPageFrame subtitle="Регистрация">
      <form
        className="space-y-4"
        noValidate
        onSubmit={handleSubmit}
      >
        <div className="space-y-2">
          <Label htmlFor="email">Электронная почта</Label>
          <Input
            autoComplete="email"
            disabled={isPendingRegister}
            error={(touched.email || submitAttempted || !!emailServerError) && !!emailVisibleError}
            id="email"
            onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
            onChange={onChangeEmail}
            placeholder="ivan@example.com"
            required
            type="email"
            value={email}
          />
          {(touched.email || submitAttempted || emailServerError) && emailVisibleError ? (
            <p className="px-3 text-xs text-[hsl(var(--error-text))]">{emailVisibleError}</p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">Телефон</Label>
          <PhoneInput
            disabled={isPendingRegister}
            error={(touched.phone || submitAttempted || Boolean(phoneServerError)) && Boolean(phoneVisibleError)}
            id="phone"
            onBlur={() => setTouched((prev) => ({ ...prev, phone: true }))}
            onChange={onChangePhone}
            required
            value={phone}
          />
          {(touched.phone || submitAttempted || phoneServerError) && phoneVisibleError ? (
            <p className="px-3 text-xs text-[hsl(var(--error-text))]">{phoneVisibleError}</p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Придумайте пароль</Label>
          <Input
            autoComplete="new-password"
            disabled={isPendingRegister}
            error={(touched.password || submitAttempted) && !!passwordError}
            id="password"
            onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Придумайте пароль"
            required
            rightIcon={
              <button
                className="flex size-5 items-center justify-center text-muted-foreground-soft transition-colors hover:text-foreground"
                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                onClick={() => setShowPassword((v) => !v)}
                type="button"
              >
                {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            }
            type={showPassword ? 'text' : 'password'}
            value={password}
          />
          {isErrorGetPasswordPolicy && (
            <Button
              onClick={() => void refetchGetPasswordPolicy()}
              type="button"
              variant="tertiary"
            >
              Загрузить требования к паролю
            </Button>
          )}
          {(touched.password || submitAttempted) && passwordError ? (
            <p className="px-3 text-xs text-[hsl(var(--error-text))]">{passwordError}</p>
          ) : null}
        </div>

        <div className="space-y-4 pt-1">
          <div className="flex flex-col gap-1">
            <label className="flex min-h-[44px] cursor-pointer items-start gap-2">
              <Checkbox
                checked={isPdAccepted}
                className="mt-0.5 size-5 shrink-0 rounded border-border-strong data-[state=checked]:border-accent data-[state=checked]:bg-accent data-[state=checked]:text-on-accent"
                id="pd-consent"
                onCheckedChange={(checked) => setIsPdAccepted(checked === true)}
              />
              <span className="text-sm leading-5 text-muted-foreground">
                Я даю согласие на обработку моих персональных данных в соответствии с
              </span>
            </label>
            <div className="pl-7">
              <a
                className="inline-flex min-h-[44px] items-center text-sm leading-5 text-accent hover:underline"
                href={personalDataConsentUrl}
                rel="noreferrer"
                target="_blank"
              >
                Согласием на обработку персональных данных
              </a>
            </div>
            {submitAttempted && pdConsentError ? (
              <p className="px-3 text-xs text-[hsl(var(--error-text))]">{pdConsentError}</p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1">
            <label className="flex min-h-[44px] cursor-pointer items-start gap-2">
              <Checkbox
                checked={isMarketingAccepted}
                className="mt-0.5 size-5 shrink-0 rounded border-border-strong data-[state=checked]:border-accent data-[state=checked]:bg-accent data-[state=checked]:text-on-accent"
                id="marketing-consent"
                onCheckedChange={(checked) => setIsMarketingAccepted(checked === true)}
              />
              <span className="text-sm leading-5 text-muted-foreground">
                Я даю согласие на получение рекламных сообщений от White Line по электронной почте и/или телефону.
              </span>
            </label>
            <div className="pl-7">
              <a
                className="inline-flex min-h-[44px] items-center text-sm leading-5 text-accent hover:underline"
                href={advertisingConsentUrl}
                rel="noreferrer"
                target="_blank"
              >
                Согласие на получение рекламных сообщений
              </a>
            </div>
          </div>
        </div>

        <Button
          className="w-full"
          disabled={isPendingRegister}
          loading={isPendingRegister}
          size="lg"
          type="submit"
        >
          {isPendingRegister ? 'Регистрируем...' : 'Зарегистрироваться'}
        </Button>

        <div className="flex flex-col gap-2 pt-1 text-sm leading-5 text-muted-foreground">
          <p>
            Нажимая «Зарегистрироваться», я принимаю{' '}
            <a
              className="inline-flex min-h-[44px] items-center text-accent hover:underline"
              href={userAgreementUrl}
              rel="noreferrer"
              target="_blank"
            >
              Пользовательское соглашение
            </a>.
          </p>
          <div>
            <a
              className="inline-flex min-h-[44px] items-center text-accent hover:underline"
              href={personalDataPolicyUrl}
              rel="noreferrer"
              target="_blank"
            >
              Политика в отношении обработки персональных данных
            </a>
          </div>
        </div>

        <p className="text-center text-sm text-muted-foreground">
          Есть аккаунт?{' '}
          <Button asChild variant="tertiary">
            <Link to="/login">Войти</Link>
          </Button>
        </p>
      </form>
    </AuthPageFrame>
  );
};

export default RegisterForm;